import {useLogout} from '@common/auth/requests/logout';
import {useResendVerificationEmail} from '@common/auth/requests/use-resend-verification-email';
import {
  useValidateEmailVerificationOtp,
  ValidateEmailVerificationOtpPayload,
} from '@common/auth/requests/use-validate-email-verification-otp';
import {useUser} from '@common/auth/ui/use-user';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {message} from '@ui/i18n/message';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {KeyboardArrowLeftIcon} from '@ui/icons/material/KeyboardArrowLeft';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {useSettings} from '@ui/settings/use-settings';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import clsx from 'clsx';
import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useRef,
  useState,
} from 'react';
import {useController, useForm} from 'react-hook-form';
import {createBrowserRouter} from 'react-router';

export const verifyEmailRouter = createBrowserRouter([
  {
    path: '*',
    element: <EmailVerificationPage />,
    hydrateFallbackElement: <FullPageLoader screen />,
  },
]);

export function EmailVerificationPage() {
  const {trans} = useTrans();
  const {data} = useUser('me');
  const resendEmail = useResendVerificationEmail();
  const {
    branding: {logo_light, logo_dark},
  } = useSettings();
  const isDarkMode = useIsDarkMode();
  const logoSrc = isDarkMode ? logo_light : logo_dark;
  const logout = useLogout();

  const form = useForm<ValidateEmailVerificationOtpPayload>();
  const validateOtp = useValidateEmailVerificationOtp(form);

  return (
    <div className="flex min-h-screen w-screen bg-alt p-24">
      <div className="mx-auto mt-40 max-w-440">
        <Button
          variant="outline"
          onClick={() => logout.mutate()}
          startIcon={<KeyboardArrowLeftIcon />}
          size="xs"
          className="mb-54 mr-auto"
        >
          <Trans message="Logout" />
        </Button>
        {logoSrc && (
          <img
            src={logoSrc}
            alt={trans(message('Site logo'))}
            className="mx-auto mb-44 block h-42 w-auto"
          />
        )}
        <div className="text-center">
          <h1 className="mb-24 text-3xl">
            <Trans message="Verify your email" />
          </h1>
          <h2 className="text-lg">
            <Trans
              message="Enter the verification code we sent to :email"
              values={{email: maskEmailAddress(data?.user.email)}}
            />
          </h2>
          <Form
            form={form}
            onSubmit={values => validateOtp.mutate(values)}
            className="my-16"
          >
            <VerificationCodeInput disabled={validateOtp.isPending} />
            <Button
              type="submit"
              variant="flat"
              color="primary"
              size="md"
              className="mt-24 w-full"
              disabled={validateOtp.isPending}
            >
              <Trans message="Verify email" />
            </Button>
          </Form>
          <div className="mb-24 text-sm">
            <Trans
              message="If you don't see the email in your inbox, check your spam folder and promotions tab. If you still don't see it, <a>request a resend</a>."
              values={{
                a: text => (
                  <Button
                    variant="link"
                    color="primary"
                    disabled={resendEmail.isPending || !data?.user.email}
                    onClick={() => {
                      resendEmail.mutate({email: data!.user.email});
                    }}
                  >
                    {text}
                  </Button>
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const verificationCodeLength = 6;

function VerificationCodeInput({disabled}: {disabled: boolean}) {
  const {trans} = useTrans();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const {
    field,
    fieldState: {error},
  } = useController<ValidateEmailVerificationOtpPayload>({
    name: 'code',
    rules: {
      required: trans(message('Enter your verification code')),
      validate: value =>
        /^\d{6}$/.test(value) ||
        trans(message('The verification code must contain 6 digits')),
    },
  });
  const [digits, setDigits] = useState<string[]>(() =>
    normalizeVerificationCode(field.value),
  );

  const updateDigits = (nextDigits: string[], focusIndex?: number) => {
    setDigits(nextDigits);
    field.onChange(nextDigits.join(''));
    if (focusIndex != null) {
      inputRefs.current[focusIndex]?.focus();
      inputRefs.current[focusIndex]?.select();
    }
  };

  const insertDigits = (value: string, startIndex: number) => {
    const insertedDigits = value.replace(/\D/g, '').slice(0, 6);
    if (!insertedDigits) return;

    const nextDigits = [...digits];
    insertedDigits.split('').forEach((digit, offset) => {
      const index = startIndex + offset;
      if (index < verificationCodeLength) {
        nextDigits[index] = digit;
      }
    });
    const lastInsertedIndex = Math.min(
      startIndex + insertedDigits.length - 1,
      verificationCodeLength - 1,
    );
    updateDigits(
      nextDigits,
      Math.min(lastInsertedIndex + 1, verificationCodeLength - 1),
    );
  };

  const handleChange = (
    index: number,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.currentTarget.value.replace(/\D/g, '');
    if (value.length > 1) {
      insertDigits(value, index);
      return;
    }

    const nextDigits = [...digits];
    nextDigits[index] = value;
    updateDigits(
      nextDigits,
      value && index < verificationCodeLength - 1 ? index + 1 : undefined,
    );
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (
      event.key === 'ArrowRight' &&
      index < verificationCodeLength - 1
    ) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    } else if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const nextDigits = [...digits];
      nextDigits[index - 1] = '';
      updateDigits(nextDigits, index - 1);
    }
  };

  const handlePaste = (
    index: number,
    event: ClipboardEvent<HTMLInputElement>,
  ) => {
    const pastedCode = event.clipboardData.getData('text');
    if (/\d/.test(pastedCode)) {
      event.preventDefault();
      insertDigits(pastedCode, index);
    }
  };

  return (
    <div className="text-left">
      <div id="verification-code-label" className="mb-6 text-sm font-medium">
        <Trans message="Code" />
      </div>
      <div
        role="group"
        aria-labelledby="verification-code-label"
        className="flex justify-between gap-8"
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={element => {
              inputRefs.current[index] = element;
              if (index === 0) field.ref(element);
            }}
            value={digit}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={index === 0 ? verificationCodeLength : 1}
            autoFocus={index === 0}
            disabled={disabled}
            aria-label={trans(
              message('Verification code digit :number', {
                values: {number: index + 1},
              }),
            )}
            aria-invalid={!!error}
            aria-describedby={error ? 'verification-code-error' : undefined}
            onChange={event => handleChange(index, event)}
            onKeyDown={event => handleKeyDown(index, event)}
            onPaste={event => handlePaste(index, event)}
            onFocus={event => event.currentTarget.select()}
            onBlur={field.onBlur}
            className={clsx(
              'h-64 min-w-0 flex-1 rounded-xl border bg text-center text-2xl font-semibold caret-primary outline-none transition duration-150',
              'focus:border-primary focus:ring-2 focus:ring-primary/30',
              'disabled:cursor-not-allowed disabled:opacity-60',
              error && 'border-danger focus:border-danger focus:ring-danger/20',
            )}
          />
        ))}
      </div>
      {error?.message && (
        <div id="verification-code-error" className="mt-6 text-xs text-danger">
          {error.message}
        </div>
      )}
    </div>
  );
}

function normalizeVerificationCode(value: string | undefined) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 6).split('');
  return Array.from(
    {length: verificationCodeLength},
    (_, index) => digits[index] || '',
  );
}

function maskEmailAddress(email: string | undefined) {
  if (!email) return '*******************';
  const [username, domain] = email.split('@');
  return `${username.slice(0, 2)}****@${domain}`;
}
